import 'cordova-plugin-purchase';
import {Device} from '@capacitor/device';
import {getProducts, createOrder, checkOrderViability} from '@/services/ec';
import i18n from '@/i18n/index';
import appEvent from '@/lib/base/appEvent';
import {
  saveLocalData,
  getLocalData,
  removeLocalData,
} from '@/lib/base/localData';

const PENDING_ORDER_STORAGE_KEY = 'iapPendingOrderContext';

const PLATFORM_MAPPING = {
  ios: {
    platform: 'ios-appstore',
    isActive: true,
  },
  android: {
    platform: 'android-playstore',
    isActive: true,
  },
};

class InAppPurchase {
  constructor() {
    this.platform = null;
    this.isActive = null;
    this.isReady = false;
    this.isLoading = false;
    this.isInit = false;
    this.ecProduct = null;
    this.ecSalePage = null;
    this.order = null;
    this.onFinished = null;
    this.onError = console.error;
  }

  get isAndroid() {
    return this.platform === 'android-playstore';
  }

  get isIOS() {
    return this.platform === 'ios-appstore';
  }

  async init() {
    if (this.isReady) return;
    if (this.isInit) {
      return new Promise((resolve) => {
        const timer = setInterval(() => {
          if (this.isReady) {
            clearInterval(timer);
            resolve();
          }
        }, 100);
      });
    }
    this.isInit = true;
    try {
      const info = await Device.getInfo();
      const config = PLATFORM_MAPPING[info.platform] || {};
      this.platform = config.platform;
      this.isActive = config.isActive;
      if (this.isActive) {
        CdvPurchase.store.verbosity = CdvPurchase.LogLevel.QUIET;
        const ecProducts = await getProducts();
        const purchaseProducts = ecProducts
          .filter((product) => product.inAppPurchaseId)
          .map((product) => {
            const {inAppPurchaseId} = product;
            return {
              type: CdvPurchase.ProductType.CONSUMABLE,
              id: inAppPurchaseId,
              platform: this.platform,
            };
          });
        CdvPurchase.store.register(purchaseProducts);
        CdvPurchase.store.validator = (...args) =>
          this.validatorHandler(...args);
        CdvPurchase.store.error(
          async (error) => this.onError && (await this.onError(error))
        );
        CdvPurchase.store
          .when()
          // .productUpdated((e) => console.log('productUpdated', e))
          // .receiptsReady((e) => console.log('receiptsReady', e)) // ios 不會觸發
          // .receiptUpdated((e) => console.log('receiptUpdated', e)) // 按下購買 或取消購買 或成功購買
          .initiated((...args) => this.initiatedHandler(...args))
          .approved((...args) => this.approvedHandler(...args))
          .verified((...args) => this.verifiedHandler(...args)) // 打後端 驗證成功
          .unverified((...args) => this.unverifiedHandler(...args)) // 打後端 驗證成功
          .finished((...args) => this.finishedHandler(...args));
        CdvPurchase.store.ready(() => (this.isReady = true));
        appEvent.on(appEvent.eventNameEnum.APP_STATE_CHANGE, ({isActive}) => {
          if (isActive) this.refresh();
        });

        await CdvPurchase.store.initialize([
          {
            platform: this.platform,
            options: {
              needAppReceipt: true,
            },
          },
        ]);
      } else {
        this.isReady = true;
      }
    } catch (e) {
      this.isActive = false;
      this.isReady = true;
    }
  }

  async refresh() {
    if (!this.isReady || !this.isActive) return;
    await CdvPurchase.store.update();
  }

  getPendingOrderContexts() {
    const raw = getLocalData(PENDING_ORDER_STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      removeLocalData(PENDING_ORDER_STORAGE_KEY);
      return {};
    }
  }

  restorePendingOrderContext(inAppPurchaseId) {
    return this.getPendingOrderContexts()[inAppPurchaseId];
  }

  savePendingOrderContext(product, salePage) {
    const contexts = this.getPendingOrderContexts();
    contexts[product.inAppPurchaseId] = {ecProduct: product, salePage};
    saveLocalData(PENDING_ORDER_STORAGE_KEY, JSON.stringify(contexts));
  }

  clearPendingOrderContext(key) {
    if (!key) return;
    const contexts = this.getPendingOrderContexts();
    delete contexts[key];
    saveLocalData(PENDING_ORDER_STORAGE_KEY, JSON.stringify(contexts));
  }

  // 下單當下還沒有 transactionId，先暫存在 inAppPurchaseId 這個 key 下面；
  // 等交易一出現（initiated）就搬到 transactionId 底下，這樣同一個商品被重複購買
  // 也不會互相蓋掉彼此的訂單/銷售頁資訊。
  migratePendingOrderContext(inAppPurchaseId, transactionId) {
    if (!inAppPurchaseId || !transactionId) return;
    const contexts = this.getPendingOrderContexts();
    if (contexts[transactionId] || !contexts[inAppPurchaseId]) return;
    contexts[transactionId] = contexts[inAppPurchaseId];
    delete contexts[inAppPurchaseId];
    saveLocalData(PENDING_ORDER_STORAGE_KEY, JSON.stringify(contexts));
  }

  getProducts() {
    if (!this.isReady) return [];
    return CdvPurchase.store.products;
  }

  getProduct(id) {
    if (!this.isReady) return undefined;
    try {
      const product = CdvPurchase.store.get(id, this.platform);
      return product;
    } catch (e) {
      return undefined;
    }
  }

  async validatorHandler(receipt, cb) {
    let response = {ok: false, message: 'Unsupported platform'};
    if (this.isIOS) {
      response = await this.appleValidatorHandler(receipt);
    }
    if (this.isAndroid) {
      response = await this.googleValidatorHandler(receipt);
    }
    cb(response);
  }
  async appleValidatorHandler(receipt) {
    const transaction = receipt.transaction;
    const transactionId = transaction.id;
    const successPayload = {
      ok: true,
      data: {
        id: receipt.transaction.id,
        latest_receipt: true,
        transaction: receipt.transaction,
      },
    };
    if (!parseInt(transactionId)) {
      return successPayload;
    }
    try {
      await this.appleCreateOrder(transactionId);
      return successPayload;
    } catch (error) {
      const errorResponse = error?.response;
      const responseMessage = errorResponse?.data?.message;
      return {
        ok: false,
        status: responseMessage?.httpStatusCode || errorResponse?.status,
        code: responseMessage?.apiError || errorResponse?.status,
        message:
          responseMessage?.errorMessage || responseMessage || error.message,
        data: {
          latest_receipt: false,
        },
      };
    }
  }
  async googleValidatorHandler(receipt) {
    // google play 會有兩種 receipt 格式
    try {
      if (receipt.transaction) {
        const transaction = receipt.transaction;
        await this.googleCreateOrder(transaction);
        return {
          ok: true,
          data: {
            id: transaction.id,
            latest_receipt: true,
            transaction,
          },
        };
      } else if (receipt.transactions?.[0]?.nativePurchase) {
        const transaction = receipt.transactions[0];
        await this.googleCreateOrder(transaction.nativePurchase);
        return {
          ok: true,
          data: {
            id: transaction.transactionId,
            latest_receipt: true,
            transaction,
          },
        };
      }
      throw new Error('No valid transaction found in receipt');
    } catch (error) {
      const errorResponse = error?.response;
      const responseMessage = errorResponse?.data?.message;
      return {
        ok: false,
        status: responseMessage?.httpStatusCode || errorResponse?.status,
        code: responseMessage?.apiError || errorResponse?.status,
        message:
          responseMessage?.errorMessage || responseMessage || error.message,
        data: {
          latest_receipt: false,
        },
      };
    }
  }

  async verifiedHandler(verifiedReceipt) {
    await verifiedReceipt.finish();
  }
  async unverifiedHandler(unverifiedReceipt) {
    const receipt = unverifiedReceipt.receipt;
    this.onError && (await this.onError(unverifiedReceipt.payload));
    await receipt.finish();
  }
  async initiatedHandler(transaction) {
    const inAppPurchaseId = transaction.products?.[0]?.id;
    this.migratePendingOrderContext(inAppPurchaseId, transaction.transactionId);
  }
  async approvedHandler(transaction) {
    if (this.isLoading) return;
    this.isLoading = true;
    if (transaction.state === 'finished') return await transaction.finish();
    const context =
      this.restorePendingOrderContext(transaction.transactionId) ||
      this.restorePendingOrderContext(transaction.products?.[0]?.id);
    if (context) {
      this.ecProduct = context.ecProduct;
      this.salePage = context.salePage;
    }
    await transaction.verify();
  }
  async finishedHandler(transaction) {
    this.isLoading = false;
    this.clearPendingOrderContext(transaction.transactionId);
    this.clearPendingOrderContext(transaction.products?.[0]?.id);
    this.ecProduct = null;
    if (this.onFinished) await this.onFinished(transaction);
  }

  async buyProduct({product, salePage}) {
    this.ecProduct = product;
    this.salePage = salePage;
    this.savePendingOrderContext(product, salePage);
    const inAppProduct = this.getProduct(product.inAppPurchaseId);
    if (this.isAndroid) {
      const transaction = await CdvPurchase.store.findInLocalReceipts(
        inAppProduct
      );
      if (transaction) await transaction.finish();
    }
    const offer = inAppProduct?.getOffer();
    if (offer) offer.order();
  }

  async appleCreateOrder(transactionId) {
    if (!this.ecProduct) return;
    const inAppProduct = this.getProduct(this.ecProduct.inAppPurchaseId);
    if (!inAppProduct) throw new Error('No inAppProduct');
    const {marketId, currencies, marketSalePageId} = this.salePage;
    const payload = {
      description:
        this.ecProduct.name[i18n.locale] ||
        Object.values(this.ecProduct.name)[0],
      products: [{id: this.ecProduct.id, count: 1, marketSalePageId}],
      platform: 'apple',
      otherInfo: {
        transactionId,
      },
      currency: currencies[0],
      marketIdOrHandle: marketId,
    };
    await createOrder(payload);
  }

  async googleCreateOrder(receipt) {
    if (!this.ecProduct) return;
    const inAppProduct = this.getProduct(this.ecProduct.inAppPurchaseId);
    if (!inAppProduct) throw new Error('No inAppProduct');
    const {marketId, currencies, marketSalePageId} = this.salePage;
    const payload = {
      description:
        this.ecProduct.name[i18n.locale] ||
        Object.values(this.ecProduct.name)[0],
      products: [{id: this.ecProduct.id, count: 1, marketSalePageId}],
      platform: 'googlePlay',
      otherInfo: {
        receipt,
        price: this.getPrice(inAppProduct),
        currency: this.getCurrency(inAppProduct),
      },
      currency: currencies[0],
      marketIdOrHandle: marketId,
    };
    await createOrder(payload);
  }

  async checkOrderViability({product, salePage}) {
    const {marketId, currencies, marketSalePageId} = salePage;
    const payload = {
      products: [{id: product.id, count: 1, marketSalePageId}],
      marketIdOrHandle: marketId,
      currency: currencies[0],
    };
    const result = await checkOrderViability(payload);
    if (!result.viability) {
      const errorMessage = result?.products?.[0]?.error;
      return {
        viability: result.viability,
        error: errorMessage,
      };
    }
    return {viability: true};
  }

  getCurrency(inAppProduct) {
    return inAppProduct?.pricing?.currency;
  }

  getPrice(inAppProduct) {
    const priceMicros = inAppProduct?.pricing?.priceMicros;
    return priceMicros / 1000000;
  }
}

export default new InAppPurchase();
